/**
 * Les deux marques servies par la même application.
 *
 * Un seul build est déployé sur les deux domaines : la marque est résolue à
 * l'exécution depuis le nom d'hôte. Ce fichier est la source unique de vérité,
 * consommé par trois endroits :
 *  - `vite.config.ts` au build (génération des manifests PWA) ;
 *  - le script injecté en tête de `index.html` (favicon, manifest, titre) ;
 *  - les composants, via `src/brand/index.ts`.
 *
 * Il ne doit donc dépendre ni de Vue, ni du navigateur, ni des alias de chemin.
 */

export type BrandId = "dr" | "inspexio";

export interface BrandManifestIcon {
  src: string;
  sizes: string;
  type: string;
  purpose: string;
}

export interface Brand {
  id: BrandId;
  /** Nom complet : fiche d'installation du navigateur et titre de l'onglet. */
  name: string;
  /** Nom sous l'icône de l'écran d'accueil, où la place est très limitée. */
  shortName: string;
  description: string;
  themeColor: string;
  backgroundColor: string;
  /**
   * Icônes d'écran d'accueil. Les URL d'un manifest se résolvent contre
   * l'URL du manifest, pas contre celle du document : elles doivent rester
   * absolues, les manifests étant servis depuis /brand/.
   */
  icons: BrandManifestIcon[];
  /** Favicon vectorielle, plus un PNG de repli pour les vieux navigateurs. */
  favicon: { svg?: string; png: string };
  appleTouchIcon: string;
  /** Onglet épinglé Safari : tracé monochrome. */
  maskIcon: { href: string; color: string };
}

// Bleu du logo : même valeur que $primary (src/style/variables.scss).
const INSPEXIO_PRIMARY = "#1E5AA8";

export const BRANDS: Record<BrandId, Brand> = {
  dr: {
    id: "dr",
    name: "DiagnosticRapid",
    shortName: "DR",
    description:
      "Inspection d'ouvrages d'art : relevés terrain, plans annotés et rapports.",
    themeColor: "#4156e2",
    backgroundColor: "#4156e2",
    // Chemins historiques, conservés tels quels : les changer ferait
    // retélécharger l'icône aux installations DR déjà en place.
    favicon: { png: "/favicon.ico" },
    appleTouchIcon: "/apple-touch-icon.png",
    maskIcon: { href: "/mask-icon.svg", color: "#FFFFFF" },
    icons: [
      {
        src: "/pwa-192x192.png",
        sizes: "192x192",
        type: "image/png",
        purpose: "any maskable",
      },
      {
        src: "/pwa-512x512.png",
        sizes: "512x512",
        type: "image/png",
        purpose: "any maskable",
      },
    ],
  },

  inspexio: {
    id: "inspexio",
    name: "Inspexio",
    shortName: "Inspexio",
    description:
      "Inspection d'ouvrages d'art : relevés terrain, plans annotés et rapports.",
    themeColor: INSPEXIO_PRIMARY,
    backgroundColor: INSPEXIO_PRIMARY,
    favicon: {
      svg: "/brand/inspexio/favicon.svg",
      png: "/brand/inspexio/favicon-32x32.png",
    },
    appleTouchIcon: "/brand/inspexio/apple-touch-icon.png",
    maskIcon: {
      href: "/brand/inspexio/mask-icon.svg",
      color: INSPEXIO_PRIMARY,
    },
    icons: [
      {
        src: "/brand/inspexio/pwa-192x192.png",
        sizes: "192x192",
        type: "image/png",
        purpose: "any",
      },
      {
        src: "/brand/inspexio/pwa-512x512.png",
        sizes: "512x512",
        type: "image/png",
        purpose: "any",
      },
      {
        src: "/brand/inspexio/pwa-maskable-512x512.png",
        sizes: "512x512",
        type: "image/png",
        purpose: "maskable",
      },
    ],
  },
};

export const DEFAULT_BRAND_ID: BrandId = "inspexio";

/** Domaine historique, en cours d'abandon au profit d'inspexio.com. */
export const LEGACY_DOMAIN = "diagnosticrapid.fr";
export const CURRENT_DOMAIN = "inspexio.com";

/**
 * Fragment de nom d'hôte qui désigne la marque historique. Partagé avec le
 * script injecté dans index.html (vite.config.ts), qui ne peut pas importer
 * ce module : les deux doivent résoudre la marque à l'identique.
 */
export const LEGACY_HOST_MARKER = "diagnosticrapid";

/**
 * Résout la marque depuis le nom d'hôte.
 *
 * Inspexio est la marque par défaut : seul le domaine historique
 * diagnosticrapid.fr (et ses sous-domaines) reste sur l'ancienne marque, le
 * temps que ses utilisateurs migrent. Tout le reste — inspexio.com, localhost,
 * un tunnel de test… — affiche ce que voient les utilisateurs d'Inspexio.
 */
export function resolveBrandId(hostname: string): BrandId {
  return hostname.toLowerCase().includes(LEGACY_HOST_MARKER)
    ? "dr"
    : DEFAULT_BRAND_ID;
}

/**
 * Sous-domaines de l'ancien domaine qui ne portent pas le même nom sur le
 * nouveau. Tout sous-domaine absent d'ici est repris tel quel.
 */
const MIGRATED_SUBDOMAINS: Record<string, string> = {
  "app-preprod": "app-staging",
};

/**
 * Adresse de la nouvelle application pour un utilisateur du domaine
 * historique : app.diagnosticrapid.fr → app.inspexio.com,
 * app-preprod.diagnosticrapid.fr → app-staging.inspexio.com. Hors domaine
 * historique (dev en « mode DiagnosticRapid »), on pointe vers la production.
 */
export function resolveMigrationUrl(hostname: string): string {
  const host = hostname.toLowerCase();
  const suffix = `.${LEGACY_DOMAIN}`;
  if (!host.endsWith(suffix)) return `https://app.${CURRENT_DOMAIN}`;

  const subdomain = host.slice(0, -suffix.length);
  return `https://${MIGRATED_SUBDOMAINS[subdomain] ?? subdomain}.${CURRENT_DOMAIN}`;
}
